/**
 * F4. Сезон на поле: в странах с долей занятых в сельском хозяйстве
 * > 30 %, в 12–70 лет при фокусе «помощь семье» или «работа», раз в год.
 * Три клетки, в каждую — одна из трёх культур с разным разбросом урожая
 * (σ 0,1 / 0,3 / 0,6 при ожидании 1,0 / 1,05 / 1,15 от посева). На кону
 * 30 % годового дохода (по 10 % на клетку); урожай — N(mean, σ), не ниже
 * нуля. Засуха (`flags.drought`, мировое событие плана 4–5) умножает
 * урожай на 0,8 / 0,6 / 0,35 — рискованная культура страдает сильнее.
 * «Пропустить» — надёжная культура всюду по ожиданию, без жребия.
 */
import { getValue, type Dataset, type GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { yearlyIncome } from '../casino/common';
import { nothing } from './common';

export const FIELD_MIN_AGE = 12;
export const FIELD_MAX_AGE = 70;
/** Доля занятых в сельском хозяйстве, %, с которой поле доступно. */
export const FIELD_MIN_AGRI_SHARE = 30;
/** Посев — доля годового дохода на все клетки. */
export const FIELD_STAKE_SHARE = 0.3;
export const FIELD_CELLS = 3;
/** Флаг мирового события «засуха» (ставит мировой слой, план 4–5). */
export const FLAG_DROUGHT = 'drought';

export interface FieldCrop {
  key: string;
  /** Ожидаемый урожай от посева. */
  mean: number;
  sigma: number;
}

export const FIELD_CROPS: readonly FieldCrop[] = [
  { key: 'minigame.field.crop.0', mean: 1.0, sigma: 0.1 },
  { key: 'minigame.field.crop.1', mean: 1.05, sigma: 0.3 },
  { key: 'minigame.field.crop.2', mean: 1.15, sigma: 0.6 },
];
/** Множитель урожая при засухе по культуре. */
export const FIELD_DROUGHT_MULT: readonly number[] = [0.8, 0.6, 0.35];

export type CropIndex = 0 | 1 | 2;

export interface FieldState {
  /** Посев на все клетки, доллары ППС. */
  stake: number;
  cells: number;
  drought: boolean;
  crops: readonly FieldCrop[];
}

export type FieldInput = { crops: CropIndex[] };

function agriShare(ds: Dataset, life: Life): number {
  try {
    return getValue(ds, life.iso3, 'agriShare', life.birthYear + life.age).v;
  } catch {
    return 0;
  }
}

function cropAt(input: FieldInput, i: number): CropIndex {
  const c = Array.isArray(input.crops) ? input.crops[i] : undefined;
  return c === 1 || c === 2 ? c : 0;
}

function harvest(state: FieldState, delta: number): MinigameOutcome {
  return {
    ...nothing(),
    moneyDelta: delta,
    logKey: state.drought ? 'minigame.field.drought' : 'minigame.field.harvest',
    logParams: { amount: Math.round(delta) },
  };
}

export const field: MinigameDef<FieldInput, FieldState> = {
  id: 'field',
  section: 'F',
  triggers: [
    { kind: 'age', min: FIELD_MIN_AGE, max: FIELD_MAX_AGE, everyYears: 1, focus: 'helpFamily' },
    { kind: 'age', min: FIELD_MIN_AGE, max: FIELD_MAX_AGE, everyYears: 1, focus: 'work' },
  ],
  available(life: Life, ds: Dataset, _law: GamblingLaw): boolean {
    if (life.age < FIELD_MIN_AGE || life.age > FIELD_MAX_AGE) return false;
    return agriShare(ds, life) > FIELD_MIN_AGRI_SHARE;
  },
  setup(life: Life, ds: Dataset): FieldState {
    return {
      stake: Math.max(0, yearlyIncome(ds, life)) * FIELD_STAKE_SHARE,
      cells: FIELD_CELLS,
      drought: life.flags[FLAG_DROUGHT] === true,
      crops: FIELD_CROPS,
    };
  },
  play(state: FieldState, input: FieldInput, _life: Life, rng: Rng): MinigameOutcome {
    const perCell = state.stake / state.cells;
    let delta = 0;
    for (let i = 0; i < state.cells; i += 1) {
      const crop = cropAt(input, i);
      const def = state.crops[crop] as FieldCrop;
      const grown = Math.max(0, rng.normal(def.mean, def.sigma)) * (state.drought ? (FIELD_DROUGHT_MULT[crop] as number) : 1);
      delta += perCell * (grown - 1);
    }
    return harvest(state, delta);
  },
  expected(state: FieldState): MinigameOutcome {
    const safe = state.crops[0] as FieldCrop;
    const grown = safe.mean * (state.drought ? (FIELD_DROUGHT_MULT[0] as number) : 1);
    return harvest(state, state.stake * (grown - 1));
  },
};
